import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

export class NetworkValidators {
	static readonly octet = '([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])';
	static readonly ipv4 = `(${this.octet}\\.){3}${this.octet}`;

	static readonly cidrMask = '\\/([1-9]|[12][0-9]|3[0-2])';
	static readonly fullMask = `\\/${this.octet}\\.${this.octet}\\.${this.octet}\\.${this.octet}`;
	static readonly subnetMask = `(${this.cidrMask}|${this.fullMask})`;

	static readonly path = '(\\/[a-zA-Z0-9-!@#"№;:?.,$%^&*()_+=\\[\\]]+)+';
	static readonly optionalPath = `(${this.path})?`;

	static readonly protocol = '(http(s?):\\/\\/www\\.|http(s?):\\/\\/|www\\.)';
	static readonly domainPart = '[a-z0-9]+([\\-\\.\\_]{1}[a-z0-9]+)*';
	static readonly tld = '\\.[a-z]{2,10}';
	static readonly domain = `(${this.domainPart}${this.tld})`;

	static readonly ipv4WithSubnet = `(${this.ipv4}(${this.subnetMask}))`;
	static readonly ipv4Only = `(${this.ipv4})`;

	static readonly ipv4WithOrWithoutSubnet = `(${this.ipv4WithSubnet}|${this.ipv4Only})`;
	static readonly urlPattern = `${this.protocol}${this.domain}${this.optionalPath}`;

	static readonly fullPattern = `^${this.ipv4WithOrWithoutSubnet}$|^${this.urlPattern}$`;

	static networkValidator(control: AbstractControl<string>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		if (!NetworkValidators.isValid(control.value)) {
			return { wrongIPv4: true };
		}
		return null;
	}

	static arrayOfIpv4Validator(control: AbstractControl<string[]>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		if (control.value.some((el) => !NetworkValidators.isValid(el, true))) {
			return { wrongIPv4: true };
		}
		return null;
	}

	static isValid(input: string, ipv4 = false): boolean {
		const syntaxRegex = !ipv4 ? new RegExp(this.fullPattern) : new RegExp(`^${this.ipv4}${this.subnetMask}$`);

		if (!syntaxRegex.test(input)) {
			return false;
		}

		if (input.includes('/')) {
			const cidrRegex = new RegExp(`^${this.ipv4}${this.subnetMask}$`);
			if (cidrRegex.test(input)) {
				return this.isValidNetworkAddress(input);
			}
		}

		return true;
	}

	static isValidNetworkAddress(cidr: string): boolean {
		const [ip, maskPart] = cidr.split('/');
		let mask: number;

		if (maskPart.includes('.')) {
			mask = this.fullMaskToCidr(maskPart);
			if (mask === -1) return false;
		} else {
			mask = parseInt(maskPart);
			if (mask < 1 || mask > 32) return false;
		}

		const ipToNumber = (ip: string): number => {
			return ip.split('.').reduce((acc, octet, index) => {
				return acc + (parseInt(octet) << (24 - 8 * index));
			}, 0);
		};

		const ipNum = ipToNumber(ip);

		const networkAddress = ipNum & (0xffffffff << (32 - mask));

		const broadcastAddress = networkAddress | (0xffffffff >>> mask);

		return ipNum === networkAddress && ipNum !== broadcastAddress;
	}

	private static fullMaskToCidr(mask: string): number {
		const octets = mask.split('.').map(Number);
		let cidr = 0;

		for (const octet of octets) {
			if (octet === 255) {
				cidr += 8;
			} else if (octet === 0) {
				break;
			} else {
				const binary = octet.toString(2).padStart(8, '0');
				if (!/^1+0+$/.test(binary)) return -1;
				cidr += binary.indexOf('0');
				break;
			}
		}

		return cidr;
	}
}

export class AdditionalValidators {
	static readonly e164Regex = /^[1-9]\d{1,14}$/;

	static readonly urlPattern =
		'^((http(s?):\\/\\/www\\.|http(s?):\\/\\/|www\\.)' +
		'(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\\.){3}([0-9]|[1-9][0-9]' +
		'|1[0-9]{2}|2[0-4][0-9]|25[0-5])(:[0-9]{2,5})?(((\\/[a-zA-Z0-9-!@#"№;:?.,$%^&*()_+=\\[\\]]*)+)$)?)$' +
		'|(http(s?):\\/\\/www\\.|http(s?):\\/\\/|www\\.)' +
		'([a-z0-9]+([\\-\\.\\_]{1}[a-z0-9]+)*\\.[a-z]{2,10}(:[0-9]{2,5})?(((\\/[a-zA-Z0-9-!@#"№;:?.,$%^&*()_+=\\[\\]]*)+)$)?)$';

	static readonly ipPattern =
		'^((http(s?):\\/\\/www\\.|http(s?):\\/\\/|www\\.)?' +
		'(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\\.){3}([0-9]|[1-9][0-9]' +
		'|1[0-9]{2}|2[0-4][0-9]|25[0-5])(:[0-9]{2,5})?(((\\/[a-zA-Z0-9-!@#"№;:?.,$%^&*()_+=\\[\\]]*)+)$)?)$' +
		'|(http(s?):\\/\\/www\\.|http(s?):\\/\\/|www\\.)?' +
		'([a-z0-9]+([\\-\\.\\_]{1}[a-z0-9]+)*\\.[a-z]{2,10}(:[0-9]{2,5})?(((\\/[a-zA-Z0-9-!@#"№;:?.,$%^&*()_+=\\[\\]]*)+)$)?)$';

	static arrayOfIpv4(control: AbstractControl<string[]>): ValidationErrors | null {
		const reg =
			/^((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/[0-9]{2,5}))$/;

		if (!Array.isArray(control.value)) {
			return { notArray: true };
		}
		return control.value.reduce((result, item) => {
			result = result || !reg.test(item);
			return result;
		}, false)
			? { wrongAddress: true }
			: null;
	}

	static checkArrayThreeNumbers(control: AbstractControl<string>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		try {
			if (!/^\[\s*(\d{3})(\s*,\s*\d{3})*\s*\]$/.test(control.value)) {
				return { array: true, message: 'Value is not a array. Check tip.' };
			}
			return null;
		} catch (e) {
			return { array: true, message: 'Validation error' };
		}
	}

	static checkArray(control: AbstractControl<string>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		try {
			if (
				!/^\[(\s*(?:"[^"]*"|[a-zA-Z_][a-zA-Z0-9_]*|\d+)(\s*,\s*(?:"[^"]*"|[a-zA-Z_][a-zA-Z0-9_]*|\d+))*)?\s*\]$/.test(
					control.value
				)
			) {
				return { array: true, message: 'Value is not a array. Check tip.' };
			}
			return null;
		} catch (e) {
			return { array: true, message: 'Validation error' };
		}
	}

	static checkJson(control: AbstractControl<string>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		try {
			const parsedValue = JSON.parse(control.value);
			if (typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
				return { json: true, message: 'Value is not a object' };
			}
			for (const [key, val] of Object.entries(parsedValue)) {
				if (!/^[\p{Letter}\d -\\]+$/u.test(key) || !/^[\p{Letter}\d -\\]+$/u.test(val as string)) {
					return { json: true, message: 'Check validation tip' };
				}
			}
			return null;
		} catch (e) {
			return { json: true, message: 'Validation error' };
		}
	}

	static uniqueArray(control: AbstractControl<any[]>): ValidationErrors | null {
		if (!control.value) {
			return null;
		}
		try {
			if (control.value.length !== new Set(control.value).size) {
				return { array: true, message: 'Check validation tip' };
			}
			return null;
		} catch (e) {
			return { array: true, message: 'Validation error' };
		}
	}

	static sumShares(control: AbstractControl): ValidationErrors | null {
		if (!(control instanceof FormArray)) {
			return null;
		}

		let totalSum = 0;
		let hasInvalidShare = false;

		control.controls.forEach((ctrl) => {
			const shareCtrl = ctrl.get('Share');
			const shareValue = shareCtrl?.value;

			if (shareValue === null || shareValue === undefined || shareValue === '') {
				shareCtrl?.setErrors({ required: true, value: 'Required error' });
				hasInvalidShare = true;
			} else if (typeof shareValue === 'number') {
				totalSum += shareValue;
			} else {
				shareCtrl?.setErrors({ mask: true, value: 'Invalid number' });
				hasInvalidShare = true;
			}
		});

		if (hasInvalidShare) {
			return { required: true, value: 'Required error' };
		}

		if (totalSum !== 100) {
			control.controls.forEach((ctrl) => {
				ctrl.get('Share')?.setErrors({ sum_shares: true, message: 'Validation error' });
			});
			return { sum_shares: true, message: 'Shares sum error' };
		} else {
			control.controls.forEach((ctrl) => {
				ctrl.get('Share')?.setErrors(null);
			});
			return null;
		}
	}

	static elementArray(pattern: RegExp | string): ValidatorFn {
		return (control: AbstractControl<any[]>): ValidationErrors | null => {
			if (!control.value || !pattern) {
				return null;
			}
			const regexp = new RegExp(pattern);
			if (control.value?.some((el: any) => typeof el === 'string' && !regexp.test(el))) {
				return { element_array: true, message: 'Element of array error' };
			}
			return null;
		};
	}

	static elementStringUnique(pattern: RegExp | string): ValidatorFn {
		return (control: AbstractControl<string>): ValidationErrors | null => {
			if (!control.value || !pattern) {
				return null;
			}
			const regexp = new RegExp(pattern);
			const elements = control.value.split('\n').map((el) => el.trim());

			if (elements.some((el) => typeof el === 'string' && !regexp.test(el))) {
				return { element_array: true, message: 'Element of array error' };
			}

			const uniqueElements = new Set(elements);
			if (uniqueElements.size !== elements.length) {
				return { element_unique: true, message: 'Elements must be unique' };
			}

			return null;
		};
	}

	static uniqueValuesArrayValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const values = control.value;
			if (!Array.isArray(values)) {
				return null;
			}
			const seen = new Set();
			for (const value of values) {
				if (seen.has(value)) {
					return { duplicateValues: true };
				}
				seen.add(value);
			}
			return null;
		};
	}

	static requiredIfOtherHasValue(otherControlName: string, dependentControlName: string): ValidatorFn {
		return (group: AbstractControl): ValidationErrors | null => {
			const otherControl = group.get(otherControlName);
			const dependentControl = group.get(dependentControlName);

			if (!otherControl || !dependentControl) {
				return null;
			}

			const otherValue = otherControl.value;
			const dependentValue = dependentControl.value;

			if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
				const isValid = dependentValue !== null && dependentValue !== undefined && dependentValue !== '';
				let errors = { ...(dependentControl.errors || {}) };
				if (isValid) {
					delete errors['requiredIfOtherHasNoValue'];
				} else {
					errors = {
						...errors,
						requiredIfOtherHasNoValue: true
					}
				}
				if (Object.keys(errors).length === 0) {
					dependentControl.setErrors(null);
				} else {
					dependentControl.setErrors(errors);
				}
				return isValid ? null : { requiredIfOtherHasNoValue: true };
			}

			const errors = { ...(dependentControl.errors || {}) };
			delete errors['requiredIfOtherHasNoValue'];
			if (Object.keys(errors).length === 0) {
				dependentControl.setErrors(null);
			} else {
				dependentControl.setErrors(errors);
			}
			return null;
		};
	}

	static greaterThanZeroValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			return value !== null && value > 0 ? null : { notGreaterThanZero: true };
		};
	}
}

export class PrefixValidators {
	static validatePrefix(control: AbstractControl<string[]>): ValidationErrors | null {
		let err = false;
		if (!control.value) {
			return null;
		}

		const ranges = control.value.toString().split(',');
		ranges.forEach((range): void => {
			const [start, end] = range.split('-').map(Number);
			if (start > end) {
				err = true;
			}
			if (
				PrefixValidators.countDigits(end) > 0 &&
				PrefixValidators.countDigits(start) !== PrefixValidators.countDigits(end)
			) {
				err = true;
			}
		});
		if (ranges.length > 1) {
			err = PrefixValidators.arrangeToArr(ranges);
		}
		return err ? { customErr: { value: control.value } } : null;
	}

	static countDigits(n: number): number {
		return n ? n.toString().length : 0;
	}

	static arrangeToArr(elements: string[]): boolean {
		const arrangedRanges: number[] = [];
		let finalArr: (number | number[])[] = [];
		for (const el of elements) {
			if (el.split('-').length > 1) {
				const [start, end] = el.split('-').map(Number);
				arrangedRanges.push(...PrefixValidators.range(start, end));
			} else {
				if (el.indexOf('!') < 0) {
					arrangedRanges.push(Number(el));
				}
			}
		}
		for (const el of elements) {
			if (el.indexOf('-') < 0 && el.indexOf('!') >= 0) {
				const clearNumber = el.substring(1, el.length);
				finalArr = arrangedRanges.filter((rangeNumber) => clearNumber.indexOf(rangeNumber.toString()) >= 0);
			}
		}
		if (finalArr.length === 0 && elements.find((el) => el.indexOf('!') >= 0) === undefined) {
			finalArr.push(arrangedRanges);
		}
		return finalArr.length <= 0;
	}

	static range(start: number, end: number): number[] {
		const len = end - start + 1;
		return Array.from({ length: len }, (_, i) => start + i);
	}
}
